import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { get, merge, set } from 'lodash';
import { MD5Service } from '../md5/md5.service';
import { DeltaService } from '../delta/delta.service';
import { ApiService } from '../api/api.service';
import { UserAuth } from './user.auth';
import { UserPrefs } from './user.prefs';
import { of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';

//eslint-disable-next-line
const profileMock = require('./layout-configuration/profile.json');
//eslint-disable-next-line
const usersMock = require('./layout-configuration/users.json');
//eslint-disable-next-line
const settingsMock = require('./layout-configuration/settings.json');
@Injectable({
  providedIn: 'root'
})
export class UserService {
  userEmailNotificationPath = '/notification/v1/operations/preferences/alerts/';
  getUserEmailNotificationPath = '/notification/v1/operations/preferences/';
  fusionLogoutUrl = '/ims/v2/logout';
  //eslint-disable-next-line
  jwtHelperService: any;
  //eslint-disable-next-line
  profileEvent: any = {
    event: '',
    user: this.userAuth
  };
  //eslint-disable-next-line
  profileEvents: BehaviorSubject<any> = new BehaviorSubject({});
  //eslint-disable-next-line
  brandingEvents: BehaviorSubject<any> = new BehaviorSubject({});
  //eslint-disable-next-line
  environment: any;

  private _hideOtherLayout = false;
  private _embedLayout = false;
  private readonly updateUserUnsubscribe: Subject<void> = new Subject();
  private readonly signoutUnsubscribe: Subject<void> = new Subject();

  constructor(
    private readonly apiService: ApiService,
    private readonly userAuth: UserAuth,
    private userPrefs: UserPrefs,
    private readonly deltaService: DeltaService,
    private readonly md5Service: MD5Service,
    private readonly dialog: MatDialog
  ) {
    this.jwtHelperService = new JwtHelperService();
  }
  //eslint-disable-next-line
  loadSettings$(): Observable<any> {
    //eslint-disable-next-line
    return of(settingsMock).pipe(
      map(response => {
        if (response) {
          sessionStorage.setItem(this.userAuth.STORAGE_KEY_SETTINGS, JSON.stringify(response));
        }
      })
    );
  }

  startDeltaInterval(): void {
    if (
      !get(this.deltaService.deltaTime.value, 'toTime', null) &&
      !get(this.deltaService.deltaTime.value, 'fromTime', null)
    ) {
      this.deltaService.stop();
      this.deltaService.run(true);
    }
  }

  getDefaultUserPrefs(): UserPrefs {
    return this.userPrefs;
  }

  //eslint-disable-next-line
  getGravatarUrl(email: any): any {
    //eslint-disable-line
    const settings = JSON.parse(sessionStorage.getItem(this.userAuth.STORAGE_KEY_SETTINGS));
    const gravatarUrl = settings && settings.gravatarUrl;
    if (gravatarUrl && email) {
      return gravatarUrl.concat(this.md5Service.hash(email));
    }
    return 'images/user_icon.png';
  }

  //eslint-disable-next-line
  setEnvironment(environment: any): void {
    this.environment = environment;
  }

  getEnvironment(): void {
    return this.environment;
  }

  //eslint-disable-next-line
  getProjectName(): any {
    return this.environment?.PROJECT_ID;
  }

  isActiveTestProject(): boolean {
    return this.environment?.PROJECT_ID === 'atweb';
  }

  isFusionUIProject(): boolean {
    return this.environment?.PROJECT_ID === 'fusion-ui';
  }

  isFusionSsoProject(): boolean {
    return this.environment?.PROJECT_ID === 'fusion-sso';
  }

  isFusionDashboardProject(): boolean {
    return this.environment?.PROJECT_ID === 'Fusion_Dashboard';
  }

  //eslint-disable-next-line
  getUserAuth(): any {
    return this.userAuth.getUserAuth();
  }

  //eslint-disable-next-line
  getUserEmail(): any {
    return get(this.userAuth, 'data.EMAIL', '');
  }

  //eslint-disable-next-line
  getUserName(): any {
    if (!get(this.userAuth, 'data.USER_ID')) {
      return this.userAuth.getUserAuth().getUserName();
    }
    return get(this.userAuth, 'data.USER_ID', '');
  }

  //eslint-disable-next-line
  updateNciToken(): any {
    if (!get(this.userAuth, 'data.USER_ID')) {
      const nciAuthToken = this.getCookie(this.userAuth.COOKIE_AUTH);
      if (nciAuthToken) {
        const nciToken = {
          token: nciAuthToken
        };
        this._setUser(nciToken);
        this._setHeaderToken(nciToken);
      }
    }
  }

  //eslint-disable-next-line
  getCookie(cname): any {
    const name = cname + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') c = c.substring(1);
      if (c.indexOf(name) == 0) return c.substring(name.length, c.length);
    }
    return null;
  }

  removeUser(): void {
    this.userAuth.clear();
    this._pushEvent('logout');
    sessionStorage.removeItem(this.userAuth.STORAGE_KEY);
    sessionStorage.removeItem(this.userAuth.STORAGE_KEY_USER_DATA);
    sessionStorage.removeItem(this.userAuth.STORAGE_KEY_USER_PREF);
    sessionStorage.removeItem('graphZoomed');
    this.removeCookies();
    localStorage.clear();
    this.dialog.closeAll();
  }

  removeCookies(): void {
    document.cookie = `${this.userAuth.COOKIE_AUTH}=;SameSite=Lax;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT`;
  }

  removeFusionUser(): void {
    this.userAuth.clear();
    sessionStorage.clear();
    this.removeCookies();
    this._pushEvent('logout');
  }

  //eslint-disable-next-line
  setCookie(cname: string, cvalue: string, exdays: number): void {
    let myCookie =
      cname + '=' + cvalue + ';SameSite=Lax;domain=' + window.location.hostname + ';path=/;';
    if (exdays) {
      const d = new Date();
      d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
      myCookie += ' expires=' + d.toUTCString();
    }
    document.cookie = myCookie;
  }

  //eslint-disable-next-line
  signin$(data: any): Observable<any> {
    //eslint-disable-next-line
    return this.apiService.post$<any>(this._getUrl('login'), data).pipe(
      //eslint-disable-next-line
      switchMap((value: any) => {
        //set user on session storage
        this._setUser(value.success);

        //push the login event for broadcasting
        this._pushEvent('login');

        //get current user infos
        const username = get(this.userAuth, 'data.USER_ID', '');
        return this._getCurrentUser$(username);
      })
    );
  }

  //eslint-disable-next-line
  getUserSettings$(id: string): Observable<any> {
    //eslint-disable-next-line
    return this.apiService.get$<any>(`/workflow/user-settings/${id}`, {}).pipe(
      map(response => {
        if (response) {
          this.userPrefs.setSettings(merge({}, this.userPrefs.defaultSettings, response));
          sessionStorage.setItem(this.userAuth.STORAGE_KEY_USER_PREF, JSON.stringify(response));

          if (id === this.getUserName()) {
            this.setThemeToStorage(response.theme || 'light');
          }
          const event = {
            event: 'user-prefs',
            username: id,
            prefs: this.getDefaultUserPrefs()
          };
          this.profileEvents.next(event);
        }
      }),
      catchError(val => of(`Error: ${val}`))
    );
  }

  updateUser(): void {
    this._getCurrentUser$(this.getUserName())
      .pipe(takeUntil(this.updateUserUnsubscribe))
      .subscribe();
  }

  signout(): void {
    this.apiService
      .delete$(this._getUrl('logout'), {})
      .pipe(
        catchError(() => of({})),
        takeUntil(this.signoutUnsubscribe)
      )
      .subscribe(
        () => {
          this.removeUser();
        },
        () => {}
      );
  }

  fusionSignout(): void {
    this.apiService
      .delete$(this.fusionLogoutUrl, {})
      .pipe(
        catchError(() => of({})),
        takeUntil(this.signoutUnsubscribe)
      )
      .subscribe(
        () => {
          this.removeFusionUser();
        },
        () => {}
      );
  }

  //eslint-disable-next-line
  getUserInfo$(username: string): Observable<any> {
    return this.apiService.get$<any>(this._getUrl('usersCtrl') + username, {}); //eslint-disable-line
  }

  //eslint-disable-next-line
  getUserBoard$(): Observable<any> {
    return of(usersMock);
  }

  //eslint-disable-next-line
  getProfileBoard$(): Observable<any> {
    return of(profileMock);
  }

  //eslint-disable-next-line
  updateWebUIPreference$(newPrefs: any, id: string): Observable<any> {
    return this.apiService.put$(`/workflow/user-settings/${id}`, newPrefs);
  }

  //eslint-disable-next-line
  getFusionUserInfo$(path: any, userName: any): Observable<any> {
    return this.apiService.get$<any>(path + userName, {}); //eslint-disable-line
  }
  getDefaultTheme(): string {
    return this.getThemeFromStorage();
  }

  getThemeFromStorage(): string {
    const theme = sessionStorage.getItem(this.userAuth.STORAGE_KEY_THEME);
    if (!theme) {
      this.setThemeToStorage();
      return 'light';
    } else {
      return theme;
    }
  }

  // I do not recommend to use this method to obtain 'actual' theme. Why? It relies on underlying
  // object UserPrefs - which has some default state and is being updated only during session.
  // if user presses F5 to reload the page, UserPrefs object looses the state.
  //eslint-disable-next-line
  getTheme(): any {
    //eslint-disable-line
    return this.userPrefs.getTheme();
  }

  setTheme(theme: string, username: string): void {
    const queryBody = { theme };
    this.userPrefs.setSettings(merge({}, this.userPrefs.userSettings, queryBody));
    const event = {
      event: 'user-prefs',
      username: username,
      prefs: this.getDefaultUserPrefs()
    };
    this.profileEvents.next(event);

    //TODO : here we must have a switchMap and subscribe should reside inside caller component
    this.updateWebUIPreference$(queryBody, username).subscribe();
  }

  //eslint-disable-next-line
  updatePreference(name: string, value: any): any {
    return this.userPrefs.chooseSaveObjectByName(name, value);
  }

  setThemeToStorage(theme?: string): void {
    if (theme) {
      sessionStorage.setItem(this.userAuth.STORAGE_KEY_THEME, theme);
    } else {
      sessionStorage.setItem(this.userAuth.STORAGE_KEY_THEME, 'light');
    }
  }

  //eslint-disable-next-line
  setPrefStorage(key: string, value: any): void {
    const userPref = this.userAuth.getUserPref();
    userPref[key] = value;
    sessionStorage.setItem(this.userAuth.STORAGE_KEY_USER_PREF, JSON.stringify(userPref));
  }
  //eslint-disable-next-line
  canManageUsersResults() {
    return this.userAuth.canManageUsersResults();
  }
  //eslint-disable-next-line
  canManagePermissions() {
    return this.userAuth.canManagePermissions();
  }
  //eslint-disable-next-line
  public getUser$(param: any): Observable<any> {
    const url = '/ims/v2/login';
    return this.apiService.post$<any>(url, param); //eslint-disable-line
  }
  //eslint-disable-next-line
  updateUserEmailNotification(userName: string, requestPayLoad) {
    return this.apiService.put$(this.userEmailNotificationPath + userName, requestPayLoad);
  }
  //eslint-disable-next-line
  getUserEmailNotification(userName: string) {
    return this.apiService.get$(this.getUserEmailNotificationPath + userName);
  }
  //eslint-disable-next-line
  userInfo$(path: any, userName: any): Observable<any> {
    return this.apiService.get$<any>(path + userName, {}); //eslint-disable-line
  }
  //eslint-disable-next-line
  getOpenIdLoginProviders$() {
    const url = '/ims/oidc/providers';
    return this.apiService.get$<any>(url); //eslint-disable-line
  }

  //eslint-disable-next-line
  getLoginProviders$() {
    const url = '/public/uicomm/getLoginProviders';
    return this.apiService.post$<any>(url, {}); //eslint-disable-line
  }

  dispose(): void {
    this.updateUserUnsubscribe.next();
    this.updateUserUnsubscribe.complete();
    this.signoutUnsubscribe.next();
    this.signoutUnsubscribe.complete();
  }

  getBrandingName(): string {
    return JSON.parse(sessionStorage.getItem('BRAND_NAME')) || '';
  }

  setBrandingName(brandingName: string): void {
    sessionStorage.setItem('BRAND_NAME', JSON.stringify(brandingName));
    this.brandingEvents.next({ dynamicBranding: true, brandName: brandingName });
  }

  get isGigatestUser(): boolean {
    return JSON.parse(sessionStorage.getItem('BRAND_SHORT_NAME')) === 'GigaTest';
  }

  setBrandShortName(shortName: string): void {
    sessionStorage.setItem('BRAND_SHORT_NAME', JSON.stringify(shortName));
  }

  set hideOtherLayout(status: boolean) {
    this._hideOtherLayout = status;
  }

  get hideOtherLayout(): boolean {
    return this._hideOtherLayout;
  }

  set embedLayout(status: boolean) {
    this._embedLayout = status;
  }

  get embedLayout(): boolean {
    return this._embedLayout;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  _setUser(data) {
    //TODO - store JWT in HTML5 Storage (sessionStorage or localStorage) or HttpOnly cookie ?
    // Ref: https://stormpath.com/blog/where-to-store-your-jwts-cookies-vs-html5-web-storage
    // Ref: https://security.stackexchange.com/questions/179498/is-it-safe-to-store-a-jwt-in-sessionstorage
    sessionStorage.setItem(this.userAuth.STORAGE_KEY, JSON.stringify(data));
    const jwtToken = data.token;

    if (jwtToken) {
      this.apiService.setHeader('Authorization', `Bearer ${jwtToken}`);
      const userData = this.jwtHelperService.decodeToken(jwtToken);
      sessionStorage.setItem(this.userAuth.STORAGE_KEY_USER_DATA, JSON.stringify(userData));

      this.userAuth.data = userData;
      this.userAuth.token = jwtToken;
      this.userAuth.settings = data.settings;

      if (data.userOrigin) {
        //Different App (SSO origin)
        this.userAuth.userOrigin = data.userOrigin;
      }

      const roles = get(this.userAuth, 'data.USER_ROLES');
      if (roles) {
        this.userAuth.data.USER_ROLES = roles;
      }
    }
  }

  private _setHeaderToken(data) {
    const jwtToken = data.token;

    if (jwtToken) {
      this.apiService.setHeader('Authorization', `Bearer ${jwtToken}`);
    }
  }

  private _setUserFirstName(name) {
    set(this.userAuth, 'data.FIRST_NAME', name);
  }

  private _setUserLastName(name) {
    set(this.userAuth, 'data.LAST_NAME', name);
  }

  private _getUrl(method) {
    const settings = JSON.parse(sessionStorage.getItem(this.userAuth.STORAGE_KEY_SETTINGS));
    if (settings) {
      return `${settings.url}${settings.paths[method]}`;
    }
    return '';
  }

  private _getNotificationsUrl(method) {
    const settings = JSON.parse(sessionStorage.getItem(this.userAuth.STORAGE_KEY_SETTINGS));
    if (settings) {
      const path = settings.notificationsManager;
      return `${path}${settings.paths[method]}`;
    }
  }

  //eslint-disable-next-line
  private _getCurrentUser$(username): Observable<any> {
    // Changed the api to call with "me" instead of {{username}} - see ATW-1644
    username = 'me';
    return (
      this.apiService
        //eslint-disable-next-line
        .get$<any>(this._getUrl('usersCtrl') + username, {})
        .pipe(
          //eslint-disable-next-line
          switchMap((value: any) => {
            if (value) {
              this._setUserFirstName(`${value.success.name || ''}`);
              this._setUserLastName(`${value.success.surname || ''}`);
              this.userAuth.setLDAPUser(`${value.success.username || ''}`);
            }
            return this.getUserSettings$(value.success.username);
          })
        )
    );
  }

  private _pushEvent(event: string) {
    this.profileEvent.event = event;
    this.profileEvents.next(this.profileEvent);
  }
}
